import axios from 'axios';
import i18n from '../i18n/index';

// Create axios instance with base configuration
const baseURL = process.env.REACT_APP_API_URL || '/api';
console.log('API Base URL:', baseURL); // Debug log

const api = axios.create({
  baseURL,
  timeout: 10000,
});

// Request interceptor to add auth token and language
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set Content-Type for non-FormData requests
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    // Add language header for backend i18n
    const currentLanguage = i18n.language;
    if (currentLanguage) {
      config.headers['Accept-Language'] = currentLanguage;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle session invalidation (401 with specific error keys)
    if (error.response?.status === 401) {
      const errorKey = error.response?.data?.errorKey;

      // Check for session invalidation or token expiration
      if (errorKey === 'errors:auth.sessionInvalidated' ||
          errorKey === 'errors:auth.tokenExpired' ||
          errorKey === 'errors:auth.invalidToken') {
        // Clear local storage and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login?session=expired';
        }
      }
    }

    // Let the AuthContext handle other 401 errors
    // This prevents automatic redirects and allows proper state management
    return Promise.reject(error);
  }
);

// Map of English Mongoose validation messages to translation keys
const VALIDATION_MESSAGE_MAP = {
  // ScheduledClass validations
  'Start time must be in the future': 'errors:scheduledClass.startInFuture',
  'End time must be after start time': 'errors:scheduledClass.endAfterStart',
  'Session type must be one of: single, recurring, multi-session': 'errors:validation.invalidInput',
  'Facility booking type must be one of: whole-facility, partial-capacity': 'errors:validation.invalidInput',
  'Status must be one of: scheduled, full, in-progress, completed, cancelled': 'errors:validation.invalidInput',
  'Frequency must be one of: daily, weekly, monthly': 'errors:validation.invalidInput',
  'Recurring end date must be after start time': 'errors:validation.invalidInput',

  // Booking validations
  'Status must be one of: confirmed, cancelled, completed, no-show': 'errors:validation.invalidInput',

  // Common validations
  'Start time must be in HH:MM format (24-hour)': 'errors:validation.invalidTimeSlot',
  'End time must be in HH:MM format (24-hour)': 'errors:validation.invalidTimeSlot',
};

// Translate validation detail messages
export const translateValidationDetail = (detail) => {
  // Check if we have a direct mapping
  if (VALIDATION_MESSAGE_MAP[detail]) {
    return i18n.t(VALIDATION_MESSAGE_MAP[detail]);
  }

  // Return original if no translation found
  return detail;
};

// Error handling function for API responses
export const handleApiError = (error) => {
  // If backend provides an errorKey, use it for translation
  if (error.response?.data?.errorKey) {
    const errorKey = error.response.data.errorKey;
    // Backend sends keys like "validation:failed" but they're in the "errors" namespace
    // Prepend "errors:" if not already present
    const translationKey = errorKey.startsWith('errors:') ? errorKey : `errors:${errorKey}`;
    const translatedMessage = i18n.t(translationKey, error.response.data.params || {});
    return translatedMessage;
  }

  // Fallback to backend message or generic error
  return error.response?.data?.error || i18n.t('errors:generic.somethingWentWrong');
};

export default api;